import type { MatchedDay, MeasureRow } from './progressionApi';
import { SUMMED_FIELDS } from './seriesConfig';
import type { RaceEventKind } from '@/Common/activityTagOptions';

/** One day on the chart: the athlete's state at the end of it, and what they did during it. */
export type ChartDay = { t: number; date: string; [field: string]: number | string | undefined };

export type Breakthrough = {
  date: string;
  name?: string;
  desc?: string;
  activity_url?: string;
  /** Medal colour, when the breakthrough earned one. */
  medal?: string;
  /** Maximal effort time in seconds: how long the athlete could hold it at their best. */
  mep?: number;
  /** The signature the activity left the athlete with (HIE in kJ), and the one before it. */
  sig?: Signature;
  sig_prev?: Signature;
  /** Set when the activity is also tagged Race or Event. */
  tag?: RaceEventKind;
};

/** An activity tagged Race or Event (Race when it has both). */
export type RaceEvent = { date: string; name?: string; activity_url?: string; kind: RaceEventKind };

export type Signature = { ftp: number; hie: number; pp: number };

/** What has been taken out of the automatic calculations for an activity, or (note) a Planner note on that day. */
export type MarkerKind = 'flagged' | 'signature' | 'tl' | 'note';
export type ManualLoad = { low: number; high: number; peak: number };

export type ActivityMarker = {
  date: string;
  name?: string;
  activity_url?: string;
  kinds: MarkerKind[];
  /** The manually set signature (HIE in kJ). */
  sig?: Signature;
  /** The manually set training load: low, high and peak. */
  tl?: ManualLoad;
  /** The Planner note's text, for the 'note' kind. */
  note?: string;
  /** Set when the training load was set by an XFAI plan (its pre-plan TL/RL preset) rather than by the athlete. */
  tl_source?: 'xfai';
};

export type ChartMarker = {
  t: number;
  name: string;
  url: string;
  kinds: MarkerKind[];
  sig?: Signature;
  tl?: ManualLoad;
  note?: string;
  tlSource?: 'xfai';
  /** Position in the list, for tying a marker to its guideline. */
  index: number;
};

export type ChartBreakthrough = {
  t: number;
  name: string;
  desc: string;
  url: string;
  medal?: string;
  mep?: number;
  sig?: Signature;
  sigPrev?: Signature;
  /** Race or Event, for a breakthrough that is also tagged one (drawn as a diamond in its colour). */
  kind?: RaceEventKind;
  /** A Race or Event that is not a breakthrough: drawn as a target rather than a diamond. */
  eventOnly?: boolean;
  /** `YYYY-MM-DD`, for the target's tooltip. */
  date?: string;
};

/**
 * The local calendar date (`YYYY-MM-DD`) of a history row. `generated_at` is already in the
 * athlete's own timezone -- reading it as text rather than as an instant keeps every day on the day
 * the athlete lived it, whatever timezone the browser happens to be in.
 */
export const rowDate = (row: { generated_at?: string; start_date?: string; date?: string }): string =>
  (row.generated_at || row.start_date || row.date || '').slice(0, 10);

/** The calendar day (`YYYY-MM-DD`) a point on the chart's time axis falls on: the reverse of `dateToChartTime`. */
export const chartTimeToDate = (time: number): string => {
  const date = new Date(time);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/** Midnight of a `YYYY-MM-DD` date in the browser's zone: the chart's axis is drawn in the browser's zone too. */
export const dateToChartTime = (date: string): number => {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day).getTime();
};

/** Text a day carries: its freshness colour, which the form line is drawn in. */
const TEXT_FIELDS = ['tsbColor'];

const inRange = (date: string, from: string, to: string, today: string, hideFuture: boolean): boolean =>
  date !== '' && (!from || date >= from) && (!to || date <= to) && !(hideFuture && date > today);

/**
 * Collapse the history to one entry per day, inside the range. Rows arrive in time order. Stress
 * and strain built up over the day are summed; everything else (loads, signature, form...) is a
 * state, so the day's last row -- where it ended up -- wins.
 */
export function buildDays(rows: MeasureRow[] | null, from: string, to: string, today: string, hideFuture: boolean): ChartDay[] {
  const byDate = new Map<string, ChartDay>();
  for (const row of rows ?? []) {
    const date = rowDate(row);
    if (!inRange(date, from, to, today, hideFuture)) continue;

    const day = byDate.get(date) ?? { t: dateToChartTime(date), date };
    for (const [field, value] of Object.entries(row)) {
      if (typeof value === 'string' && TEXT_FIELDS.includes(field)) {
        day[field] = value;
        continue;
      }
      if (typeof value !== 'number' || !isFinite(value)) continue;
      day[field] = SUMMED_FIELDS.includes(field) ? ((day[field] as number | undefined) ?? 0) + value : value;
    }
    byDate.set(date, day);
  }
  return [...byDate.values()].sort((a, b) => a.t - b.t);
}

export function buildBreakthroughs(items: Breakthrough[] | null, from: string, to: string, today: string, hideFuture: boolean, raceEvents: RaceEvent[] | null = null): ChartBreakthrough[] {
  const inView = (date: string) => inRange(date, from, to, today, hideFuture);
  const breakthroughs: ChartBreakthrough[] = (items ?? [])
    .filter((item) => inView(rowDate(item)))
    .map((item) => ({
      t: dateToChartTime(rowDate(item)),
      name: item.name ?? '',
      desc: item.desc ?? '',
      url: item.activity_url ?? '',
      medal: item.medal,
      mep: typeof item.mep === 'number' ? item.mep : undefined,
      sig: item.sig,
      sigPrev: item.sig_prev,
      kind: item.tag,
      date: rowDate(item),
    }));
  // A race or event that is also a breakthrough is already drawn, as a diamond carrying its kind.
  const drawn = new Set((items ?? []).map((item) => item.activity_url).filter(Boolean));
  const targets: ChartBreakthrough[] = (raceEvents ?? [])
    .filter((event) => inView(rowDate(event)) && !(event.activity_url && drawn.has(event.activity_url)))
    .map((event) => ({
      t: dateToChartTime(rowDate(event)),
      name: event.name ?? '',
      desc: '',
      url: event.activity_url ?? '',
      kind: event.kind,
      eventOnly: true,
      date: rowDate(event),
    }));
  return [...breakthroughs, ...targets].sort((a, b) => a.t - b.t);
}

/** Whether a mark is a breakthrough (as against a Race or Event that isn't one). */
export const isBreakthrough = (mark: ChartBreakthrough): boolean => !mark.eventOnly;

/**
 * What the breakthrough strip draws for the two chips. Both on: everything. Breakthroughs only: the diamonds, without
 * their Race/Event marking. Race / Event only: just the targets, a tagged breakthrough as a target too.
 */
export function markersToShow(marks: ChartBreakthrough[], breakthroughsOn: boolean, raceEventsOn: boolean): ChartBreakthrough[] {
  if (breakthroughsOn && raceEventsOn) return marks;
  if (breakthroughsOn) return marks.filter(isBreakthrough).map((mark) => (mark.kind ? { ...mark, kind: undefined } : mark));
  if (raceEventsOn) return marks.filter((mark) => mark.kind).map((mark) => ({ ...mark, eventOnly: true }));
  return [];
}

/**
 * With a filter on, split each day's XSS bar: what the matching activities did stays in the low /
 * high / peak colours, and the rest of the day's XSS becomes `xout`, drawn on top in grey.
 */
export function mergeMatchedDays(days: ChartDay[], matched: MatchedDay[] | null): ChartDay[] {
  if (!matched) return days;
  const byDate = new Map(matched.map((day) => [day.date, day]));
  return days.map((day) => {
    const total = typeof day.xss === 'number' ? day.xss : ['xlss', 'xhss', 'xpss'].reduce((sum, field) => sum + ((day[field] as number) || 0), 0);
    if (!total) return day;
    const mine = byDate.get(day.date);
    return {
      ...day,
      xlss: mine?.xlss ?? 0,
      xhss: mine?.xhss ?? 0,
      xpss: mine?.xpss ?? 0,
      xout: Math.max(0, total - (mine?.xss ?? 0)),
    };
  });
}

export function buildMarkers(items: ActivityMarker[] | null, from: string, to: string, today: string, hideFuture: boolean): ChartMarker[] {
  return (items ?? [])
    .filter((item) => inRange(rowDate(item), from, to, today, hideFuture))
    .map((item) => ({
      t: dateToChartTime(rowDate(item)),
      name: item.name ?? '',
      url: item.activity_url ?? '',
      kinds: item.kinds,
      sig: item.sig,
      tl: item.tl,
      note: item.note,
      tlSource: item.tl_source,
    }))
    .sort((a, b) => a.t - b.t)
    .map((marker, index) => ({ ...marker, index }));
}
